import { daysInWeek, minutesInDay, minutesInHour } from "date-fns/constants"
import { TFunction } from "i18next"
import { useCareProviderWithOriginQuery$data } from "@/modules/care-provider/services/__generated__/useCareProviderWithOriginQuery.graphql"
import { DayOfWeek } from "@/types/__generated__/union-types"

type CareProvider = NonNullable<useCareProviderWithOriginQuery$data["ch_careProviderByRelationalId"]>
export type DaySchedule = NonNullable<CareProvider["detailedSchedule"]>[number]

const MIDNIGHT = "00:00"
const SCHEDULE_SCAN_DAYS = daysInWeek + 1
const SUNDAY_JS_INDEX = 0
export const DAYS_ORDER: DayOfWeek[] = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"]

const getDayOfWeek = (now: Date): DayOfWeek => {
  const dayIndex = now.getDay()
  return DAYS_ORDER[dayIndex === SUNDAY_JS_INDEX ? daysInWeek - 1 : dayIndex - 1]
}

const getNextDay = (day: DayOfWeek): DayOfWeek => {
  const index = DAYS_ORDER.indexOf(day)
  return DAYS_ORDER[(index + 1) % daysInWeek]
}

const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(":").map(Number)
  return hours * minutesInHour + minutes
}

const getTimeMinutes = (now: Date): number => {
  return now.getHours() * minutesInHour + now.getMinutes()
}

type EventType = "OPENS" | "CLOSES"
interface OpeningEvent {
  type: EventType
  time: string
  minutesFromNow: number
}
interface AbsolutePeriod {
  startAbs: number
  endAbs: number
  startTime: string
  endTime: string
}

const collectAbsolutePeriods = (detailedSchedule: ReadonlyArray<DaySchedule>, today: DayOfWeek): AbsolutePeriod[] => {
  const periods: AbsolutePeriod[] = []
  let day = today
  for (let dayIndex = 0; dayIndex < SCHEDULE_SCAN_DAYS; dayIndex++) {
    const dayOffset = dayIndex * minutesInDay
    const schedule = detailedSchedule.find((s) => s.day === day)
    if (schedule?.workingType === "OPEN24_HOURS") {
      periods.push({
        startAbs: dayOffset,
        endAbs: dayOffset + minutesInDay,
        startTime: MIDNIGHT,
        endTime: MIDNIGHT,
      })
    } else if (schedule?.workingType === "HOURLY" && schedule.periods?.length) {
      for (const period of schedule.periods) {
        periods.push({
          startAbs: dayOffset + timeToMinutes(period.start),
          endAbs: dayOffset + timeToMinutes(period.end),
          startTime: period.start,
          endTime: period.end,
        })
      }
    }
    day = getNextDay(day)
  }
  return periods
}

const getNextOpenAndClose = (detailedSchedule: ReadonlyArray<DaySchedule>, now: Date): OpeningEvent[] => {
  const periods = collectAbsolutePeriods(detailedSchedule, getDayOfWeek(now))
  const nowMinutes = getTimeMinutes(now)
  let nextOpen: OpeningEvent | undefined
  let nextClose: OpeningEvent | undefined

  for (let i = 0; i < periods.length; i++) {
    const period = periods[i]
    const continuesFromPrev = i > 0 && periods[i - 1].endAbs === period.startAbs
    const continuesIntoNext = i + 1 < periods.length && period.endAbs === periods[i + 1].startAbs

    if (!nextOpen && !continuesFromPrev && period.startAbs > nowMinutes) {
      nextOpen = { type: "OPENS", time: period.startTime, minutesFromNow: period.startAbs - nowMinutes }
    }
    if (!nextClose && !continuesIntoNext && period.endAbs > nowMinutes) {
      nextClose = { type: "CLOSES", time: period.endTime, minutesFromNow: period.endAbs - nowMinutes }
    }
    if (nextOpen && nextClose) break
  }

  return [nextOpen, nextClose]
    .filter((event): event is OpeningEvent => event !== undefined)
    .sort((a, b) => a.minutesFromNow - b.minutesFromNow)
}

const formatEvent = (event: OpeningEvent, t: TFunction): string => {
  if (event.minutesFromNow < minutesInDay) {
    return event.type === "OPENS"
      ? t("careProvider.openingHours.opensAt", { time: event.time })
      : t("careProvider.openingHours.closesAt", { time: event.time })
  }
  const hours = Math.round(event.minutesFromNow / minutesInHour)
  return event.type === "OPENS"
    ? t("careProvider.openingHours.opensIn", { hours })
    : t("careProvider.openingHours.closesIn", { hours })
}

export const getScheduleSummary = (
  detailedSchedule: ReadonlyArray<DaySchedule>,
  t: TFunction,
  now: Date = new Date(),
): string => {
  const allOpen24 = detailedSchedule.every((s) => s.workingType === "OPEN24_HOURS")
  if (allOpen24) {
    return t("careProvider.openingHours.open247")
  }

  const allClosed = detailedSchedule.every((s) => s.workingType === "CLOSED")
  if (allClosed) {
    return t("careProvider.openingHours.mightBeClosedPermanently")
  }

  const events = getNextOpenAndClose(detailedSchedule, now)
  return events.map((event) => formatEvent(event, t)).join(" • ")
}
